import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api, { errMsg } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { setPageMeta } from '../../lib/shop';

const inp = "w-full border hairline px-4 py-3 text-sm bg-white focus:outline-none focus:border-[#0B0F12]";

function AuthShell({ title, sub, children, testid }) {
  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid={testid}>
      <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95] text-center">VQ Health Account</p>
      <h1 className="font-display font-black uppercase text-4xl tracking-tight mt-2 text-center">{title}</h1>
      {sub && <p className="text-sm text-[#575B60] text-center mt-2">{sub}</p>}
      <div className="mt-8">{children}</div>
    </div>
  );
}

function GoogleButton() {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/account';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button data-testid="google-login-button" onClick={start}
      className="w-full border border-[#0B0F12] py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#F4F4F0] transition-colors flex items-center justify-center gap-3">
      <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.3h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.8z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1l-3.9 3C3.3 21.3 7.3 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.3l3.9-3z"/><path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.7l3.9 3c.9-2.9 3.6-5 6.8-5z"/></svg>
      Continue with Google
    </button>
  );
}

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const { login, user, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setPageMeta('Sign in');
    if (user) navigate(isAdmin ? '/admin' : '/account');
  }, [user, isAdmin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      navigate(['owner', 'admin'].includes(u.role) ? '/admin' : '/account');
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Sign In" testid="login-page">
      <GoogleButton />
      <div className="flex items-center gap-3 my-5">
        <span className="flex-1 border-t hairline" /><span className="font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">or</span><span className="flex-1 border-t hairline" />
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="login-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} />
        <input data-testid="login-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inp} />
        <button data-testid="login-submit" disabled={busy} className="w-full bg-[#0B0F12] text-white py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors disabled:opacity-50">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <div className="flex justify-between mt-4 font-mono2 text-[11px] uppercase tracking-wider">
        <Link to="/forgot-password" data-testid="forgot-link" className="text-[#575B60] hover:text-[#0B0F12]">Forgot password</Link>
        <Link to="/register" data-testid="register-link" className="text-[#575B60] hover:text-[#0B0F12]">Create account</Link>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { setPageMeta('Create account'); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success('Account created — check your inbox to verify your email.');
      navigate('/account');
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Create Account" sub="Order tracking, saved compounds and faster checkout." testid="register-page">
      <GoogleButton />
      <div className="flex items-center gap-3 my-5">
        <span className="flex-1 border-t hairline" /><span className="font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95]">or</span><span className="flex-1 border-t hairline" />
      </div>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="register-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
        <input data-testid="register-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} />
        <input data-testid="register-password" type="password" required minLength={8} placeholder="Password (min 8 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} />
        <button data-testid="register-submit" disabled={busy} className="w-full bg-[#0B0F12] text-white py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors disabled:opacity-50">
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </form>
      <p className="text-[11px] text-[#8A8F95] mt-4 leading-relaxed">By creating an account you confirm you are 18 or over and understand all products are for in-vitro laboratory research only.</p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => { setPageMeta('Reset password'); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post('/auth/forgot', { email }); } catch {}
    setDone(true);
  };
  return (
    <AuthShell title="Reset Password" testid="forgot-page">
      {done ? (
        <p className="text-sm text-[#575B60] text-center" data-testid="forgot-sent">If an account exists for {email}, a reset link has been sent.</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="forgot-email" type="email" required placeholder="Your account email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} />
          <button data-testid="forgot-submit" className="w-full bg-[#0B0F12] text-white py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors">Send reset link</button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { setPageMeta('Choose new password'); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/reset', { token: params.get('token'), password });
      setDone(true);
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <AuthShell title="New Password" testid="reset-page">
      {done ? (
        <p className="text-sm text-[#1F8A58] text-center" data-testid="reset-success">Password updated — redirecting to sign in…</p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="reset-password-input" type="password" required minLength={8} placeholder="New password (min 8 characters)" value={password} onChange={(e) => setPassword(e.target.value)} className={inp} />
          <button data-testid="reset-submit" className="w-full bg-[#0B0F12] text-white py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors">Update password</button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState('working');
  useEffect(() => {
    setPageMeta('Verify email');
    api.get('/auth/verify', { params: { token: params.get('token') } })
      .then(() => setState('ok')).catch(() => setState('fail'));
  }, [params]);
  return (
    <AuthShell title="Email Verification" testid="verify-page">
      <p className="text-center text-sm" data-testid="verify-result">
        {state === 'working' && 'Verifying…'}
        {state === 'ok' && <span className="text-[#1F8A58]">Email verified — thank you. <Link to="/account" className="underline">Go to your account</Link>.</span>}
        {state === 'fail' && <span className="text-[#E54D2E]">This verification link is invalid or has expired.</span>}
      </p>
    </AuthShell>
  );
}
